import type { RfxQuoteColumn, RfxQuoteItem } from "../../types/quote.type";
import type {
	QuoteTableColumn,
	QuoteTableRow,
} from "../../../components/QuoteTable/quote-table.types";
import type { ReadOnlyRow } from "./components/ReadOnlyQuoteTable";
import type { SummaryTotalRow } from "../../../components/QuoteTable/QuoteSummaryTotals";
import type { TotalRow } from "../../../components/QuoteTable/DynamicQuoteTable";
import {
	TOTAL_ROWS,
	TotalType,
} from "newui/features/Rfx/components/QuoteTable";
import { calculateGST, calculateTotalWithGST } from "../../../config/gst.config";
import {
	getDescriptionCellValue,
	getRowDisplayName,
} from "newui/features/Rfx/components/QuoteTable/utils";
import { formatCurrency } from "utils/helpers";

/**
 * Converts a flat QuoteCellData array into a nested lookup: { [rowId]: { [columnId]: value } }.
 * Used to prepare cell revision data for getCellDiff().
 */
export const cellDataToLookup = (
	cells: {
		rfx_quote_column_id: number;
		rfx_quote_row_id: number;
		value: string | null;
	}[]
): Record<number, Record<number, string>> => {
	const result: Record<number, Record<number, string>> = {};
	for (const cell of cells) {
		if (!result[cell.rfx_quote_row_id]) {
			result[cell.rfx_quote_row_id] = {};
		}
		result[cell.rfx_quote_row_id][cell.rfx_quote_column_id] = cell.value ?? "";
	}
	return result;
};

/**
 * Returns a Set of diff keys for cell-based revisions.
 * Keys are in the format "cell-{rowId}-{columnId}".
 */
export const getCellDiff = (
	currentCells: Record<number, Record<number, string>>,
	previousCells: Record<number, Record<number, string>>
): Set<string> => {
	const diffKeys = new Set<string>();

	const allRowIds = new Set([
		...Object.keys(currentCells).map(Number),
		...Object.keys(previousCells).map(Number),
	]);

	allRowIds.forEach((rowId) => {
		const currentRow = currentCells[rowId] ?? {};
		const previousRow = previousCells[rowId] ?? {};

		const allColIds = new Set([
			...Object.keys(currentRow).map(Number),
			...Object.keys(previousRow).map(Number),
		]);

		allColIds.forEach((colId) => {
			const currentVal = currentRow[colId] ?? "";
			const previousVal = previousRow[colId] ?? "";
			if (currentVal !== previousVal) {
				diffKeys.add(`cell-${rowId}-${colId}`);
			}
		});
	});

	return diffKeys;
};

/**
 * Converts cell edit values into QuoteTableRow[] for rendering.
 * Uses quote row definitions for names, sorts, etc.
 */
export const mapCellSnapshotToRows = (
	cellValues: Record<number, Record<number, string>>,
	quoteItems?: RfxQuoteItem[],
	columns?: RfxQuoteColumn[]
): QuoteTableRow[] => {
	if (!quoteItems) {
		return [];
	}

	// Primary description column = lowest sortOrder description column. The
	// row's "name" for display is the value of that cell in the snapshot.
	const descCol = columns
		?.filter((c) => c.columnType === "description")
		.sort((a, b) => a.sortOrder - b.sortOrder)[0];

	return quoteItems
		.map((item) => {
			const rowCells = cellValues[item.id] ?? {};

			// Merge procurer-fixed cells (rfxInviteId === null) so procurer-
			// authored description / read-only column values appear in the
			// revision view even though the revision snapshot only contains
			// supplier-owned cells. Snapshot values take precedence.
			const merged: Record<number, string> = {};
			for (const cell of item.cells ?? []) {
				if (cell.rfxInviteId === null && cell.value != null) {
					merged[cell.rfxQuoteColumnId] = cell.value;
				}
			}
			for (const [colId, val] of Object.entries(rowCells)) {
				merged[Number(colId)] = val;
			}

			// Display name: supplier-owned primary description wins over
			// procurer fallback (handled by getRowDisplayName).
			const snapshotName = descCol ? rowCells[descCol.id] : undefined;
			const fallbackName = getRowDisplayName(
				item,
				columns as unknown as RfxQuoteColumn[]
			);
			const name =
				snapshotName != null && snapshotName !== ""
					? snapshotName
					: fallbackName;

			return {
				itemId: item.id,
				name,
				unitLabel: item.unitMeasurement,
				quantity: 0,
				rate: 0,
				createdByType: item.createdByType ?? undefined,
				columnValues: merged,
				sort_order: Number(item.sortOrder),
			};
		})
		.sort((a, b) => a.sort_order - b.sort_order);
};

/**
 * Canonical per-row "total" value, mirroring QuoteTableCell and the backend
 * RfxQuoteResponseTotalService. Numeric response cells = response_value +
 * saving (savings are stored negative, so they subtract). The quantity
 * multiplier applies ONLY when the table has exactly one numeric response
 * column AND a quantity column with qty > 0; otherwise every numeric cell is
 * summed flat (so a table with no quantity column still totals correctly).
 *
 * `rowVals` is the Formik row shape ({ quantity, rate_<colId> }).
 */
export const computeRowTotalFromRowVals = (
	rowVals: Record<string, number | string> | undefined,
	columns: { id: number; columnType: string }[]
): number => {
	const numericResponseCols = columns.filter(
		(c) => c.columnType === "response_value" || c.columnType === "saving"
	);
	const hasQtyCol = columns.some((c) => c.columnType === "response_quantity");
	const rawQty = hasQtyCol ? Number(rowVals?.quantity ?? 0) : 0;
	const useMultiplier =
		numericResponseCols.length === 1 && hasQtyCol && rawQty > 0;
	return numericResponseCols.reduce((sum, c) => {
		const val = Number(rowVals?.[`rate_${c.id}`] ?? 0);
		return sum + (useMultiplier ? rawQty * val : val);
	}, 0);
};

/**
 * Resolves a single cell's display value from Formik values.
 */
const resolveCellValue = (
	col: QuoteTableColumn,
	row: RfxQuoteItem,
	formikRow: Record<string, number | string> | undefined,
	allColumns: QuoteTableColumn[],
	unitLookup?: Map<number, string>
): string => {
	switch (col.columnType) {
		case "description": {
			// Resolve THIS specific description column's cell — quotes can have
			// more than one description column (e.g. "Item" + "Notes/Exclusions")
			// and each must show its own value. The shared helper handles the
			// supplier-wins merge per (row, columnId).
			const desc = getDescriptionCellValue(row, col.id);
			if (desc) return desc;
			// Fallback: primary description (covers legacy rows that only carry
			// a name on the primary column).
			return (
				getRowDisplayName(row, allColumns as unknown as RfxQuoteColumn[]) || "-"
			);
		}

		case "unit_of_measurement": {
			const unitVal = formikRow?.unit;
			if (unitVal != null && unitLookup) {
				return unitLookup.get(Number(unitVal)) ?? "-";
			}
			return row.unitMeasurement ?? "-";
		}

		case "response_quantity":
			return String(formikRow?.quantity ?? 0);

		case "response_value":
			return formatCurrency(Number(formikRow?.[`rate_${col.id}`] ?? 0));

		case "saving": {
			// Savings share the `rate_${col.id}` Formik key with response_value
			// (stored as a negative). Display as an absolute currency value.
			const raw = Number(formikRow?.[`rate_${col.id}`] ?? 0);
			return formatCurrency(Math.abs(raw));
		}

		case "response_number":
			return String(formikRow?.[`number_${col.id}`] ?? 0);

		case "response_text":
			return String(formikRow?.[`text_${col.id}`] ?? "");

		case "total":
			// Canonical per-row total — matches the supplier's editable cell and
			// stops collapsing to $0 when there is no quantity column.
			return formatCurrency(computeRowTotalFromRowVals(formikRow, allColumns));

		default:
			return "-";
	}
};

/**
 * Transforms quote rows + Formik values into ReadOnlyRow[] for ReadOnlyQuoteTable.
 */
export const buildReadOnlyRows = (
	rows: RfxQuoteItem[],
	columns: QuoteTableColumn[],
	allValues: Record<string, Record<string, number | string>>,
	unitLookup?: Map<number, string>,
	isSupplier = false
): ReadOnlyRow[] => {
	const sorted = [...columns].sort((a, b) => a.sortOrder - b.sortOrder);

	return rows.map((row) => {
		const key = `quote-${row.id}`;
		const formikRow = allValues?.[key];

		return {
			rowId: row.id,
			isSupplierRow: isSupplier || row.createdByType === "supplier",
			cells: sorted.map((col) => ({
				columnId: col.id,
				displayValue: resolveCellValue(
					col,
					row,
					formikRow,
					columns,
					unitLookup
				),
			})),
		};
	});
};

/**
 * Resolves the numeric value for a single summary row (subtotal / GST / total)
 * from a subtotal. Single source of truth for the GST + grand-total arithmetic
 * shared by every quote summary renderer.
 */
const resolveSummaryRowValue = (rowId: TotalType, subtotal: number): number => {
	switch (rowId) {
		case TotalType.SubTotal:
			return subtotal;
		case TotalType.GST:
			return calculateGST(subtotal);
		case TotalType.Total:
			return calculateTotalWithGST(subtotal);
		default:
			return 0;
	}
};

/**
 * Builds pre-formatted SummaryTotalRow[] (used by ReadOnlyQuoteTable) from
 * TOTAL_ROWS and a subtotal value.
 */
export const buildReadOnlyTotals = (subtotal: number): SummaryTotalRow[] =>
	TOTAL_ROWS.map((row) => ({
		id: row.id,
		label: row.label,
		color: row.color,
		displayValue: formatCurrency(resolveSummaryRowValue(row.id, subtotal)),
	}));

/**
 * Builds numeric TotalRow[] (used by DynamicQuoteTable's `totals` prop, which
 * formats the value itself) from TOTAL_ROWS and a subtotal value.
 */
export const buildSummaryTotalRows = (subtotal: number): TotalRow[] =>
	TOTAL_ROWS.map((row) => ({
		id: row.id,
		name: row.label,
		color: row.color,
		value: resolveSummaryRowValue(row.id, subtotal),
	}));

/**
 * Computes per-column totals for response_value columns from Formik values.
 * Used by the ReadOnlyQuoteTable disabled branch in QuoteTab.
 */
export const calculateReadOnlyColumnTotals = (
	columns: QuoteTableColumn[],
	items: RfxQuoteItem[],
	allValues: Record<string, Record<string, number | string>>,
	supplierColumnTotals?: Record<number, number>
): Record<number, number> => {
	const totalsMap: Record<number, number> = {};
	const responseValueCols = columns.filter(
		(c) => c.columnType === "response_value" || c.columnType === "saving"
	);

	for (const col of responseValueCols) {
		let sum = 0;
		for (const item of items) {
			const formikVal = allValues[`quote-${item.id}`]?.[`rate_${col.id}`];
			const val = Number(formikVal ?? 0);
			if (!isNaN(val)) {
				sum += val;
			}
		}
		totalsMap[col.id] = sum + (supplierColumnTotals?.[col.id] ?? 0);
	}

	return totalsMap;
};

/**
 * Converts QuoteTableRow[] to ReadOnlyRow[] for ReadOnlyQuoteTable.
 */
export const mapQuoteTableRowsToReadOnly = (
	rows: QuoteTableRow[],
	columns: QuoteTableColumn[]
): ReadOnlyRow[] => {
	// Primary description column = lowest sortOrder description column.
	const primaryDescColId = [...columns]
		.filter((c) => c.columnType === "description")
		.sort((a, b) => a.sortOrder - b.sortOrder)[0]?.id;

	return rows.map((row) => ({
		rowId: row.itemId,
		cells: columns.map((col) => {
			let displayValue: string;

			if (col.columnType === "description") {
				// Multi-description support: each description column shows its
				// OWN cell value (looked up via columnValues). The primary
				// description falls back to row.name for legacy rows.
				const cellVal = row.columnValues?.[col.id];
				if (cellVal != null && cellVal !== "") {
					displayValue = cellVal;
				} else if (col.id === primaryDescColId) {
					displayValue = row.name || "-";
				} else {
					displayValue = "-";
				}
			} else if (col.columnType === "unit_of_measurement") {
				displayValue = row.unitLabel ?? "-";
			} else if (col.columnType === "response_value") {
				const raw = row.columnValues?.[col.id];
				displayValue =
					raw != null && raw !== "" ? formatCurrency(Number(raw)) : "-";
			} else if (col.columnType === "saving") {
				// Stored negative; display as absolute currency.
				const raw = row.columnValues?.[col.id];
				displayValue =
					raw != null && raw !== ""
						? formatCurrency(Math.abs(Number(raw)))
						: "-";
			} else if (col.columnType === "total") {
				// `total` is computed, not stored. Derive from this row's other
				// columnValues: quantity × Σ response_value rates + Σ savings.
				let quantity = 0;
				let rateSum = 0;
				let savingSum = 0;
				let hasQty = false;
				for (const c of columns) {
					const v = Number(row.columnValues?.[c.id] ?? 0) || 0;
					if (c.columnType === "response_quantity") {
						hasQty = true;
						quantity = v;
					} else if (c.columnType === "response_value") {
						rateSum += v;
					} else if (c.columnType === "saving") {
						savingSum += v; // already negative
					}
				}
				const total = hasQty
					? quantity * rateSum + savingSum
					: rateSum + savingSum;
				displayValue = formatCurrency(total);
			} else {
				// response_quantity, response_number, response_text — raw value
				displayValue = row.columnValues?.[col.id] ?? "-";
			}

			return { columnId: col.id, displayValue };
		}),
		isSupplierRow: row.createdByType === "supplier",
	}));
};
